/**
 * Shared buy-box winner determination logic
 * Used by both ServiceCard.tsx and service detail pages ([id].tsx)
 * to ensure consistent perk display across the application
 */

export interface Perk {
  id: string;
  name: string;
  description?: string;
  perk_type:
    | 'rebate'
    | 'gift_card'
    | 'free_service'
    | 'included_service'
    | 'service'
    | 'geographic'
    | string;
  rebate_amount?: number;
  recurring_amount?: number;
  billing_cycle?: string;
  duration_months?: number;
  geographic_scope_type?: string;
  is_active: boolean;
  partner_name?: string;
  applied_at?: string;
  created_at?: string;
  score?: number;
}

const BUY_BOX_TYPE_PRIORITY: Record<string, number> = {
  included_service: 0,
  free_service: 1,
  gift_card: 2,
  rebate: 3,
};

function getPerkTypePriority(perkType?: string): number {
  return BUY_BOX_TYPE_PRIORITY[perkType || ''] ?? 3;
}

function getPerkValue(perk: Perk): number {
  return perk.rebate_amount || perk.recurring_amount || 0;
}

function getPerkTimestamp(perk: Perk): number {
  return new Date(perk.applied_at || perk.created_at || 0).getTime();
}

/**
 * Neutral client-side ordering for buy-box options.
 * Prefer server score when present, then apply a small explicit service-first
 * preference without auto-prioritizing cash perks.
 */
export function compareBuyBoxPerks(a: Perk, b: Perk): number {
  const aScore = Number.isFinite(a.score) ? Number(a.score) : null;
  const bScore = Number.isFinite(b.score) ? Number(b.score) : null;

  if (aScore !== null || bScore !== null) {
    const scoreDelta = (bScore ?? Number.NEGATIVE_INFINITY) - (aScore ?? Number.NEGATIVE_INFINITY);
    if (scoreDelta !== 0) return scoreDelta;
  }

  const priorityDelta = getPerkTypePriority(a.perk_type) - getPerkTypePriority(b.perk_type);
  if (priorityDelta !== 0) return priorityDelta;

  const valueDelta = getPerkValue(b) - getPerkValue(a);
  if (valueDelta !== 0) return valueDelta;

  const dateDelta = getPerkTimestamp(b) - getPerkTimestamp(a);
  if (dateDelta !== 0) return dateDelta;

  return (a.name || '').localeCompare(b.name || '');
}

/**
 * Determines the buy-box winner from a list of perks
 * Uses the same neutral ordering policy as the service detail hook.
 *
 * @param perks - Array of perks to evaluate
 * @returns The winning perk, or null if no perks
 */
export function determineBuyBoxWinner(perks: Perk[]): Perk | null {
  if (!perks || perks.length === 0) {
    return null;
  }

  if (perks.length === 1) {
    return perks[0];
  }

  const sortedPerks = [...perks].sort(compareBuyBoxPerks);

  return sortedPerks[0];
}

/**
 * Gets all perks except the winning perk
 * Used to display alternative perks
 *
 * @param perks - Array of all perks
 * @param winningPerk - The winning perk to exclude
 * @returns Array of alternative perks
 */
export function getAlternativePerks(perks: Perk[], winningPerk: Perk | null): Perk[] {
  if (!perks || !winningPerk) {
    return perks || [];
  }

  return perks.filter((perk) => perk.id !== winningPerk.id);
}

/**
 * Determines if a service has a buy-box winner
 *
 * @param perks - Array of perks for the service
 * @returns true if the service has perks and thus a winner
 */
export function hasBuyBoxWinner(perks: Perk[]): boolean {
  return perks && perks.length > 0;
}
